import { isAxiosError } from 'axios';

import { type IApiThrowsError, IErrorDefaultContent } from '../services';

export const ForbiddenActionError =
  "Ops, you don't have permission to execute this action.";

export const UnexpectedError =
  'Unexpected error occurred. Please try again later.';

export const ServerError = 'Server error occurred. Please try again later.';

export const DefaultDescription =
  'An error occurred. Please try again or contact Platform support.';

export const DefaultDescriptionBenefitEducation =
  'An error occurred. Please try again or contact Benefit Education support.';

export const InvalidTokenError =
  'Your session has expired. Please log in again.';

export const ResourceNotFoundError =
  'This resource no longer exists or has been removed.';

const SYMFONY_ENTITY_NOT_FOUND_INDICATORS = [
  'EntityValueResolver',
  'object not found by',
];

const getErrorMessage = (data: IErrorDefaultContent): string | undefined => {
  return data.error?.message || data.error?.title || data.title || data.message;
};

const getFullResponseMessage = (data: IErrorDefaultContent): string => {
  const parts = [
    data.message,
    data.title,
    data.error?.message,
    data.error?.title,
    (data as IErrorDefaultContent & { detail?: string }).detail,
  ].filter(Boolean);
  return parts.join(' ');
};

const isSymfonyEntityNotFoundError = (
  data: IErrorDefaultContent | undefined
): boolean => {
  if (!data || typeof data !== 'object') return false;
  const fullMessage = getFullResponseMessage(data);
  return SYMFONY_ENTITY_NOT_FOUND_INDICATORS.some((indicator) =>
    fullMessage.includes(indicator)
  );
};

const getResourceNotFoundDescription = (resourceName: string): string => {
  const name = resourceName.trim().toLowerCase();
  return name ? `This ${name} no longer exists.` : ResourceNotFoundError;
};

export const formatUnknownErrorMessage = (
  error: unknown,
  defaultDescription: string = DefaultDescription,
  resourceName?: string
): string => {
  let description = UnexpectedError;
  if (!isAxiosError(error)) {
    if (error instanceof Error) {
      description = error.message ?? UnexpectedError;
    }
  } else {
    const { response } = error as IApiThrowsError;
    if (response) {
      const { status, data } = response;

      if (status === 404 || isSymfonyEntityNotFoundError(data)) {
        description = resourceName
          ? getResourceNotFoundDescription(resourceName)
          : ResourceNotFoundError;
      } else if (status >= 500) {
        description = ServerError;
      } else if (getErrorMessage(data) === 'Invalid JWT Token') {
        description = InvalidTokenError;
      } else {
        description = getErrorMessage(data) ?? defaultDescription;
      }
    }
  }
  return description;
};
